import React, { useState, useEffect, useRef } from 'react';
import CloseIcon from '../../assets/images/x.svg';
import { GettingNativeAsset } from '../../utils/constants';
import { getAssetImage } from '../../utils/cropUtils';
import AssetWithNetworkBadge from '../common/AssetWithNetworkBadge';

export default function AddAssetModal({
  onClose,
  data = [],
  onSelectAsset,
  selectedAsset,
  initialSelectedAsset = null,
  blockchains = [],
  handleConfirmSelection,
  isSubmitting = false,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredAssets, setFilteredAssets] = useState(data);
  const dropdownRef = useRef(null);

  // Helper function to get blockchain for an asset
  const getBlockchainForAsset = (asset) => {
    return blockchains.find((bc) => bc.id === asset.blockchainId);
  };

  // Set initial selected asset when prop changes
  useEffect(() => {
    if (initialSelectedAsset) {
      onSelectAsset(initialSelectedAsset);
    }
  }, [initialSelectedAsset]);

  // Filter assets based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredAssets(data);
    } else {
      const filtered = data.filter(
        (asset) =>
          asset.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.displaySymbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.onchain.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.legacyId.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredAssets(filtered);
    }
  }, [searchTerm, data]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAssetSelect = (asset) => {
    onSelectAsset(asset);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  return (
    <div>
      {/* Backdrop */}
      <div
        className="fixed z-50 inset-0 bg-black opacity-30 w-full h-full"
        onClick={() => !isSubmitting && onClose(false)}
      ></div>

      {/* Modal */}
      <div className="z-51 fixed inset-0 flex items-center justify-center pointer-events-none p-3 sm:p-4">
        <div className="pointer-events-auto w-full max-w-[614px] bg-white rounded-xl shadow-xl max-h-[95vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-[#CDD9F0] flex items-center justify-between flex-shrink-0">
            <p className="text-base sm:text-lg text-[#181D27] font-medium">
              {initialSelectedAsset ? 'Cambiar activo' : 'Añade los activos'}
            </p>
            <button
              onClick={() => !isSubmitting && onClose(false)}
              disabled={isSubmitting}
              className={`p-1 rounded-md transition-colors flex-shrink-0 ${
                isSubmitting ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#E9EAEB]'
              }`}
            >
              <img src={CloseIcon} alt="Close" className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Search Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-xs sm:text-sm font-medium text-[#181D27] mb-2">
                Buscar activo
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-[#6B7280]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                <input
                  type="text"
                  className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm border border-[#E9EAEB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#092F7B] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Buscar por nombre o símbolo..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsDropdownOpen(true);
                  }}
                  onFocus={() => setIsDropdownOpen(true)}
                  disabled={isSubmitting}
                />
              </div>

              {/* Dropdown List */}
              {isDropdownOpen && !isSubmitting && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-[#E9EAEB] rounded-lg shadow-lg max-h-[250px] sm:max-h-[300px] overflow-y-auto">
                  {filteredAssets.length > 0 ? (
                    filteredAssets.map((asset) => {
                      if (asset.assetClass !== 'NATIVE') {
                        const blockchain = getBlockchainForAsset(asset);
                        return (
                          <div
                            key={asset._id}
                            className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-[#F9FAFB] cursor-pointer transition-colors border-b border-[#F3F4F6] last:border-b-0"
                            onClick={() => handleAssetSelect(asset)}
                          >
                            {/* Asset Image with Network Badge */}
                            <div className="relative w-10 h-10 sm:w-12 sm:h-12">
                              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#F9FAFB] border border-[#E9EAEB] flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {getAssetImage(asset) ? (
                                  <img
                                    src={getAssetImage(asset)}
                                    alt={asset.displayName}
                                    className="w-5 h-5 sm:w-6 sm:h-6 object-contain"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<span class="text-xs font-semibold text-[#6B7280]">${asset.displaySymbol.substring(
                                        0,
                                        2
                                      )}</span>`;
                                    }}
                                  />
                                ) : (
                                  <span className="text-xs font-semibold text-[#6B7280]">
                                    {asset.displaySymbol.substring(0, 2)}
                                  </span>
                                )}
                              </div>

                              {/* Network Badge Overlay */}
                              <div className="absolute bottom-1.5 right-1.5 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-white border border-[#E9EAEB] flex items-center justify-center overflow-hidden shadow-sm">
                                <img
                                  src={GettingNativeAsset(asset.legacyId).icon}
                                  alt={GettingNativeAsset(asset.legacyId).assetName}
                                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            </div>

                            {/* Asset Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5">
                                <p className="text-xs sm:text-sm font-medium text-[#181D27] truncate">
                                  {asset.displayName}
                                </p>
                                <span className="px-1.5 sm:px-2 py-0.5 bg-[#EFF6FF] text-[#092F7B] rounded text-[10px] sm:text-xs font-mono font-medium flex-shrink-0">
                                  {GettingNativeAsset(asset.legacyId).assetName}
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                                <p className="text-[10px] sm:text-xs text-[#6B7280] truncate">
                                  {asset.displaySymbol}
                                </p>
                                {blockchain && (
                                  <>
                                    <span className="text-[#6B7280] text-xs hidden sm:inline">
                                      •
                                    </span>
                                    <div className="flex items-center gap-1">
                                      {blockchain.metadata?.media?.[0]?.url && (
                                        <img
                                          src={blockchain.metadata.media[0].url}
                                          alt={blockchain.displayName}
                                          className="w-2.5 h-2.5 sm:w-3 sm:h-3 object-contain"
                                          onError={(e) => (e.target.style.display = 'none')}
                                        />
                                      )}
                                      <span className="text-[10px] sm:text-xs text-[#6B7280] truncate">
                                        {blockchain.displayName}
                                      </span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Check icon if selected */}
                            {selectedAsset?.id === asset.id && (
                              <svg
                                className="w-4 h-4 sm:w-5 sm:h-5 text-[#092F7B] flex-shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            )}
                          </div>
                        );
                      }
                    })
                  ) : (
                    <div className="px-4 py-6 sm:py-8 text-center text-[#6B7280]">
                      <p className="text-xs sm:text-sm">No se encontraron activos</p>
                      <p className="text-[10px] sm:text-xs mt-1">
                        Intenta con otro término de búsqueda
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Asset Details */}
            {selectedAsset && (
              <div className="border border-[#E9EAEB] rounded-lg p-3 sm:p-4 bg-[#F9FAFB] space-y-2.5 sm:space-y-3 animate-fadeIn">
                <p className="text-xs sm:text-sm font-medium text-[#181D27] mb-2 sm:mb-3">
                  Detalles del activo seleccionado
                </p>

                {/* Asset Header */}
                <div className="flex items-center gap-2.5 sm:gap-3 pb-2.5 sm:pb-3 border-b border-[#E9EAEB]">
                  <AssetWithNetworkBadge
                    asset={selectedAsset}
                    getAssetImage={getAssetImage}
                    GettingNativeAsset={GettingNativeAsset}
                    size="large"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                      <p className="text-sm sm:text-base font-semibold text-[#181D27] break-words">
                        {selectedAsset.displayName}
                      </p>
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-[#EFF6FF] text-[#092F7B] rounded text-[10px] sm:text-xs font-mono font-semibold flex-shrink-0">
                        {GettingNativeAsset(selectedAsset.legacyId).assetName}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#6B7280]">
                      {selectedAsset.displaySymbol}
                    </p>
                  </div>
                </div>

                {/* Blockchain Info */}
                {(() => {
                  const blockchain = getBlockchainForAsset(selectedAsset);
                  return blockchain ? (
                    <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-[#E9EAEB]">
                      <p className="text-[10px] sm:text-xs font-medium text-[#6B7280] mb-1.5 sm:mb-2">
                        Base Asset / Blockchain
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#F9FAFB] border border-[#E9EAEB] flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {blockchain.metadata?.media?.[0]?.url ? (
                            <img
                              src={blockchain.metadata.media[0].url}
                              alt={blockchain.displayName}
                              className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-[#6B7280]">${blockchain.legacyId.substring(
                                  0,
                                  2
                                )}</span>`;
                              }}
                            />
                          ) : (
                            <span className="text-xs font-bold text-[#6B7280]">
                              {blockchain.legacyId.substring(0, 2)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-[#181D27] break-words">
                            {blockchain.displayName}
                          </p>
                          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-[#6B7280] flex-wrap">
                            <span className="truncate">{blockchain.legacyId}</span>
                            <span className="hidden sm:inline">•</span>
                            <span className="truncate">{blockchain.onchain.protocol}</span>
                            {blockchain.onchain.chainId && (
                              <>
                                <span className="hidden sm:inline">•</span>
                                <span className="truncate">
                                  Chain ID: {blockchain.onchain.chainId}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Asset Details Grid */}
                <div className="space-y-1.5 sm:space-y-2">
                  <DetailItem
                    label="Network"
                    value={GettingNativeAsset(selectedAsset.legacyId).assetName}
                    copyable
                  />
                  <DetailItem label="Símbolo" value={selectedAsset.onchain.symbol} />
                  <DetailItem label="Nombre completo" value={selectedAsset.onchain.name} />
                  <DetailItem
                    label="Dirección del contrato"
                    value={selectedAsset.onchain.address}
                    copyable
                  />
                  <DetailItem label="Decimales" value={selectedAsset.onchain.decimals} />
                  <DetailItem
                    label="Estándares"
                    value={selectedAsset.onchain.standards.join(', ')}
                  />
                  <DetailItem
                    label="Clase de activo"
                    value={
                      selectedAsset.assetClass === 'FT'
                        ? 'Token Fungible'
                        : selectedAsset.assetClass
                    }
                  />
                  {selectedAsset.metadata.verified !== undefined && (
                    <DetailItem
                      label="Verificado"
                      value={selectedAsset.metadata.verified ? 'Sí' : 'No'}
                      badge={selectedAsset.metadata.verified}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0 border-t border-[#E9EAEB] pt-3 sm:pt-0 sm:border-t-0">
            <button
              onClick={() => onClose(false)}
              disabled={isSubmitting}
              className={`w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 border border-[#E9EAEB] text-[#181D27] rounded-lg text-sm font-medium transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#F9FAFB]'
              }`}
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={!selectedAsset || isSubmitting}
              className={`w-full sm:w-auto px-4 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                selectedAsset && !isSubmitting
                  ? 'bg-[#092F7B] text-white hover:bg-[#0a3d99]'
                  : 'bg-[#E9EAEB] text-[#9CA3AF] cursor-not-allowed'
              }`}
            >
              {isSubmitting && (
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isSubmitting ? 'Procesando...' : initialSelectedAsset ? 'Actualizar' : 'Confirmar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for displaying detail items
function DetailItem({ label, value, copyable = false, badge = false }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-start justify-between py-1.5 sm:py-2 gap-2">
      <span className="text-[10px] sm:text-xs font-medium text-[#6B7280] w-2/5 sm:w-1/3 flex-shrink-0">
        {label}
      </span>
      <div className="text-[10px] sm:text-xs text-[#181D27] w-3/5 sm:w-2/3 text-right flex items-center justify-end gap-1.5 sm:gap-2 min-w-0">
        {badge && (
          <span className="px-1.5 sm:px-2 py-0.5 bg-[#D1FAE5] text-[#065F46] rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
            Verificado
          </span>
        )}
        {copyable ? (
          <>
            <span
              className="truncate max-w-[120px] sm:max-w-[200px] font-mono text-[10px] sm:text-xs"
              title={value}
            >
              {value}
            </span>
            <button
              onClick={handleCopy}
              className="p-0.5 sm:p-1 hover:bg-white rounded transition-colors flex-shrink-0"
              title="Copiar"
            >
              {copied ? (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-[#059669]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              ) : (
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-[#6B7280]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              )}
            </button>
          </>
        ) : (
          <span className={`${badge ? '' : 'font-medium'} break-words text-right`}>{value}</span>
        )}
      </div>
    </div>
  );
}
